<template>
	<div class="uf-drawer" :class="{'uf-drawer-expanded': expanded}" ref="drawer" id="drawer">
		<div class="uf-drawer-above" v-if="hasAbove" ref="drawerAbove">
			<slot name="above"></slot>
		</div>
		<v-container class="uf-drawer-content" ref="drawerContent">
			<v-card>
				<div class="uf-drawer-top" v-if="hasTop" ref="drawerTop">
					<div class="uf-drawer-toggle" @click="toggle">
						<v-icon>
							{{ isOpen ? 'keyboard_arrow_down' : 'keyboard_arrow_up' }}
						</v-icon>
					</div>
					<slot name="top"></slot>
				</div>
				<slot></slot>
			</v-card>
		</v-container>
	</div>
</template>

<script>
  import TweenLite from 'gsap/TweenLite';
  import Draggable from 'gsap/Draggable';
  import ThrowPropsPlugin from 'gsap/ThrowPropsPlugin';

  const plugins = ThrowPropsPlugin;

  const HEADER_HEIGHT = 90;

  export default {
    name: 'UfDrawer',
    props: {
      offset: { type: Number, default: 0 },
      expanded: { type: Boolean, default: false },
    },
    data: () => ({
      totalOffset: 0,
      dragger: null,
	  transitioning: false,
	  isOpen: false,
    }),
    watch: {
      expanded(value) {
        if (value) {
          this.open();
        } else {
          this.close();
        }
        return value;
      },
    },
    computed: {
      hasAbove() {
        return !!this.$slots.above;
      },
      hasTop() {
        return !!this.$slots.top;
      },
    },
    methods: {
      toggle() {
		if(this.transitioning) return false;

		if(this.isOpen) {
		  this.close();
		}else{
		  this.open();
		}

	  },
      open() {
        if (!this.dragger) return;

        this.transitioning = true;
        const top = this.$refs.drawer.clientHeight - (window.innerHeight - HEADER_HEIGHT) < 0
          ? this.dragger[0].vars.bounds.top
          : HEADER_HEIGHT;

        TweenLite.to(this.$refs.drawer, 0.5, { y: top, onComplete: () => {
			this.transitioning = false;
            this.isOpen = true;
          }});
      },
      close() {
        if (!this.dragger) return;

        this.transitioning = true;
        TweenLite.to(this.$refs.drawer, 0.5, {
          y: window.innerHeight + this.totalOffset,
          onComplete: () => {
            this.isOpen = false;
            this.transitioning = false;
            this.$emit('close');
          },
        });
      },
      measure() {
        this.totalOffset = this.offset;
        if (this.hasAbove) this.totalOffset -= this.$refs.drawerAbove.offsetHeight;
        if (this.hasTop) this.totalOffset -= this.$refs.drawerTop.offsetHeight;

        if (this.expanded) {
          TweenLite.to(this.$refs.drawer, 0, { y: 0 });
        } else {
          TweenLite.to(this.$refs.drawer, 0, {
            y: window.innerHeight + this.totalOffset,
          });
        }

        this.$nextTick(() => {
          if (this.dragger && this.dragger[0]) {
            this.dragger[0].applyBounds({
              top: window.innerHeight - this.$refs.drawer.offsetHeight,
              left: 0,
              width: 0,
              height: (this.$refs.drawer.offsetHeight * 2) + this.totalOffset,
            });
          }
        });
      },
    },
    mounted() {
      this.measure();

      this.dragger = Draggable.create(this.$refs.drawer, {
        type: 'y',
        throwProps: true,
        allowNativeTouchScrolling: false,
        zIndexBoost: false,
        minimumMovement: 6,
        bounds: {
          top: window.innerHeight - this.$refs.drawer.offsetHeight,
          left: 0,
          width: 0,
          height: (this.$refs.drawer.offsetHeight * 2) + this.totalOffset,
        },
        snap: (value) => {
          if (value < HEADER_HEIGHT) return value;
          if (this.dragger[0].getDirection() === 'up') {
            this.$emit('open');
            return HEADER_HEIGHT;
          }
          return window.innerHeight;
        },
        onThrowComplete: () => {
          if (this.dragger[0].y >= window.innerHeight * .666) {
            this.isOpen = false;
            this.$emit('close');
          }else{
            this.isOpen = true;
		  }
        },
        onPress: () => this.$emit('press'),
      });

    },
  };
</script>

<style lang="stylus">
	.uf-drawer {
		position fixed
		width: 100%
		top: 0
		background: linear-gradient(to bottom, rgba(#EFF2F5, 0) 0, rgba(#EFF2F5, 1) 75px)
		z-index: 0
		transition: background .5s .3s

		&:before {
			content: ''
			position: absolute
			top: -50px;
			left: 0;
			height: 100%;
			width: 100%;
			background: linear-gradient(to bottom, rgba(#EFF2F5, 0) 0, rgba(#EFF2F5, 1) 75px)
			z-index: 0
			transition: background .5s .3s
		}

		&.is-inverted {
			background: linear-gradient(to bottom, rgba(#000, 0) 0, rgba(#000, .5) 75px)

			&:before {
				background: linear-gradient(to bottom, rgba(#000, 0) 0, rgba(#000, .5) 75px)
			}
		}

		.uf-drawer-above {
			position relative
			padding: 0 32px 16px 32px
		}

		.uf-drawer-content {
			padding: 0
		}

		.uf-drawer-above ~ .uf-drawer-content {
			padding-top: 0
		}

		.v-card {
			border-radius: 32px 32px 0 0

			.uf-drawer-toggle {
				position: absolute
				top: .5rem
				left: 50%
				transform: translateX(-50%)
				z-index: 1
			}

			.v-card__title {
				padding: 16px

				&.has-top-whitespace {
					padding: 32px 24px 24px 24px
				}
			}

		}
	}
</style>
